"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import { AlertTriangle } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/auth-context";
import { subscribeSetting } from "@/lib/v3/reads";

/**
 * Shown when Stripe reports a failed payment or subscription is past_due.
 * Subscription access may still be granted during Smart Retries (see subscription-access).
 */
export function StripePaymentIssueBanner() {
  const { user } = useAuth();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!user) return;
    const unsub = subscribeSetting(user.uid, "subscription", (data) => {
      const status = data?.status as string | undefined;
      const hasError = data?.lastPaymentError != null;
      setVisible(status === "past_due" || hasError);
    });
    return () => unsub();
  }, [user]);

  if (!visible || !user) return null;

  return (
    <Alert
      variant="default"
      className="mb-4 border-amber-500/50 bg-amber-50 text-amber-950 dark:bg-amber-950/30 dark:text-amber-50 dark:border-amber-600/50"
    >
      <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400" />
      <AlertTitle>Payment issue</AlertTitle>
      <AlertDescription className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <span>
          We could not charge your card for OfficialsPay. Update your payment
          method in billing so you do not lose access.
        </span>
        <Button
          asChild
          size="sm"
          variant="outline"
          className="shrink-0 border-amber-700/40 dark:border-amber-400/40"
        >
          <Link href="/account">Manage billing</Link>
        </Button>
      </AlertDescription>
    </Alert>
  );
}
